import React, {
  useState,
  useEffect,
  useRef,
  useImperativeHandle,
  forwardRef,
} from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Divider,
  Box,
  Typography,
  CircularProgress,
  Alert,
} from '@mui/material';
import { ListBoxComponent } from '@syncfusion/ej2-react-dropdowns';
import { getApiClient } from '../../services/api/axios-config';
import SwimLaneAssignmentTable from './SwimLaneAssignmentTable';

/**
 * ProjectTeamDualListBox - A dual list box for assigning team members to a project
 * Uses Syncfusion ListBoxComponent for drag-and-drop functionality
 */
const ProjectTeamDualListBox = forwardRef(
  ({ authToken, projectId, userId, onAssignmentsChange }, ref) => {
    const availableListRef = useRef(null);
    const assignedListRef = useRef(null);
    const isMountedRef = useRef(true);

    const [availableEmployees, setAvailableEmployees] = useState([]);
    const [assignedEmployees, setAssignedEmployees] = useState([]);
    const [loading, setLoading] = useState(false);

    // Track mount state to prevent state updates after unmount
    useEffect(() => {
      isMountedRef.current = true;
      return () => {
        isMountedRef.current = false;
      };
    }, []);

    // Fetch project team members and available employees
    useEffect(() => {
      const fetchData = async () => {
        if (!authToken || !projectId) return;

        setLoading(true);
        try {
          const apiClient = getApiClient(authToken);

          // Fetch current team members
          const teamResponse = await apiClient.get(
            `/api/projects/${projectId}/team`
          );

          // Fetch all active employees
          const employeesResponse = await apiClient.get('/api/employees');

          // Check if still mounted before updating state
          if (!isMountedRef.current) return;

          const teamMembers = teamResponse.data.data?.teamMembers || [];
          // Employees API might return data at different path
          const allEmployees =
            employeesResponse.data.data?.employees ||
            employeesResponse.data.employees ||
            employeesResponse.data ||
            [];

          console.log('Team members from API:', teamMembers);
          console.log('All employees from API:', allEmployees);

          // Map to list box format - filter out any with null IDs
          // Team API returns employee_id, Employees API returns id
          const teamMemberIds = teamMembers
            .filter((m) => m?.employee_id)
            .map((m) => m.employee_id);

          const assigned = teamMembers
            .filter((m) => m?.employee_id && m?.lastname && m?.firstname)
            .map((m) => ({
              id: m.employee_id,
              text: `${m.firstname} ${m.lastname}`,
            }))
            .sort((a, b) => a.text.localeCompare(b.text));

          // Employees API uses 'id' field
          const available = allEmployees
            .filter(
              (e) =>
                (e?.id || e?.employee_id) &&
                e?.lastname &&
                e?.firstname &&
                !teamMemberIds.includes(e.id || e.employee_id)
            )
            .map((e) => ({
              id: e.id || e.employee_id,
              text: `${e.firstname} ${e.lastname}`,
            }))
            .sort((a, b) => a.text.localeCompare(b.text));

          console.log('Processed assigned:', assigned);
          console.log('Processed available:', available);

          setAssignedEmployees(assigned);
          setAvailableEmployees(available);
        } catch (error) {
          console.error('Error fetching project team data:', error);
        } finally {
          if (isMountedRef.current) {
            setLoading(false);
          }
        }
      };

      fetchData();
    }, [authToken, projectId]);

    const fields = { text: 'text', value: 'id' };

    // Get current assigned employees directly from ListBox (avoids stale closure)
    const getCurrentAssignedFromListBox = () => {
      const items = Array.from(assignedListRef.current?.getItems() || []);
      return items
        .map((item) => {
          const data = assignedListRef.current?.getDataByValue(
            item.getAttribute('data-value')
          );
          return data;
        })
        .filter(Boolean);
    };

    // Notify parent when assignments change, reading directly from ListBox
    const notifyParentFromListBox = () => {
      // Don't notify if component is unmounted
      if (!isMountedRef.current) return;

      const currentAssigned = getCurrentAssignedFromListBox();
      console.log('=== notifyParentFromListBox called ===');
      console.log('currentAssigned from ListBox:', currentAssigned);

      // Convert to team member format for swim lane table
      // Parse "firstname lastname" format back to firstname and lastname
      const teamMemberFormat = currentAssigned.map((emp) => {
        const nameParts = (emp.text || '').trim().split(' ');
        const lastname = nameParts.length > 1 ? nameParts.slice(-1)[0] : '';
        const firstname =
          nameParts.length > 1
            ? nameParts.slice(0, -1).join(' ')
            : nameParts[0] || '';
        return {
          employee_id: emp.id,
          firstname: firstname || '',
          lastname: lastname || '',
        };
      });
      console.log('teamMemberFormat:', teamMemberFormat);
      onAssignmentsChange?.(teamMemberFormat);
    };

    const onDropAvailable = () => {
      // Employee moved TO available (removed from team)
      // Wait for ListBox to update, then read current state
      setTimeout(() => {
        if (isMountedRef.current) {
          notifyParentFromListBox();
        }
      }, 150);
    };

    const onDropAssigned = () => {
      // Employee moved TO assigned (added to team)
      // Wait for ListBox to update, then read current state
      setTimeout(() => {
        if (isMountedRef.current) {
          notifyParentFromListBox();
        }
      }, 150);
    };

    // Expose methods to parent
    useImperativeHandle(ref, () => ({
      getAssignedEmployeeIds: () => {
        const items = Array.from(assignedListRef.current?.getItems() || []);
        return items
          .map((item) => {
            const data = assignedListRef.current?.getDataByValue(
              item.getAttribute('data-value')
            );
            return data?.id;
          })
          .filter(Boolean);
      },

      saveAssignments: async () => {
        try {
          const assignedItems = Array.from(
            assignedListRef.current?.getItems() || []
          );
          const employeeIds = assignedItems
            .map((item) => {
              const data = assignedListRef.current?.getDataByValue(
                item.getAttribute('data-value')
              );
              return data?.id;
            })
            .filter(Boolean);

          const apiClient = getApiClient(authToken);

          await apiClient.post(`/api/projects/${projectId}/team`, {
            employeeIds,
            userId,
          });

          return { success: true, employeeIds };
        } catch (error) {
          console.error('Error saving project team:', error);
          throw error;
        }
      },
    }));

    if (loading) {
      return (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress size={24} />
        </Box>
      );
    }

    return (
      <Box
        sx={{
          display: 'flex',
          gap: 3,
          justifyContent: 'center',
          width: '100%',
          boxSizing: 'border-box',
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0, maxWidth: '50%' }}>
          <Typography variant="subtitle1" gutterBottom>
            Available Employees
          </Typography>
          <ListBoxComponent
            ref={availableListRef}
            dataSource={availableEmployees}
            fields={fields}
            allowDragAndDrop={true}
            allowFiltering={true}
            filtering={(e) => {
              if (!e.text) {
                e.updateData(availableEmployees);
                return;
              }
              const query = e.text.toLowerCase();
              const filteredData = availableEmployees
                .filter((item) => item.text.toLowerCase().includes(query))
                .sort((a, b) => a.text.localeCompare(b.text));
              e.updateData(filteredData);
            }}
            scope="project-team-list"
            height="300px"
            drop={onDropAvailable}
            change={(e) => {
              if (e.isInteracted) {
                setTimeout(() => {
                  if (!isMountedRef.current) return;
                  const currentItems =
                    availableListRef.current?.getItems() || [];
                  const currentIds = currentItems.map((item) => {
                    const data = availableListRef.current?.getDataByValue(
                      item.getAttribute('data-value')
                    );
                    return data?.id;
                  });
                  const allEmployees = [
                    ...availableEmployees,
                    ...assignedEmployees,
                  ];
                  setAvailableEmployees(
                    allEmployees
                      .filter((emp) => currentIds.includes(emp.id))
                      .sort((a, b) => a.text.localeCompare(b.text))
                  );
                  const newAssigned = allEmployees
                    .filter((emp) => !currentIds.includes(emp.id))
                    .sort((a, b) => a.text.localeCompare(b.text));
                  setAssignedEmployees(newAssigned);
                  // Use ListBox reader instead of stale closure
                  setTimeout(() => {
                    if (isMountedRef.current) {
                      notifyParentFromListBox();
                    }
                  }, 100);
                }, 50);
              }
            }}
          />
        </Box>

        <Box sx={{ flex: 1, minWidth: 0, maxWidth: '50%' }}>
          <Typography variant="subtitle1" gutterBottom>
            Project Team Members
          </Typography>
          <ListBoxComponent
            ref={assignedListRef}
            dataSource={assignedEmployees}
            fields={fields}
            allowDragAndDrop={true}
            allowFiltering={true}
            filtering={(e) => {
              if (!e.text) {
                e.updateData(assignedEmployees);
                return;
              }
              const query = e.text.toLowerCase();
              const filteredData = assignedEmployees
                .filter((item) => item.text.toLowerCase().includes(query))
                .sort((a, b) => a.text.localeCompare(b.text));
              e.updateData(filteredData);
            }}
            scope="project-team-list"
            height="300px"
            drop={onDropAssigned}
            change={(e) => {
              if (e.isInteracted) {
                setTimeout(() => {
                  if (!isMountedRef.current) return;
                  const currentItems =
                    assignedListRef.current?.getItems() || [];
                  const currentIds = currentItems.map((item) => {
                    const data = assignedListRef.current?.getDataByValue(
                      item.getAttribute('data-value')
                    );
                    return data?.id;
                  });
                  const allEmployees = [
                    ...availableEmployees,
                    ...assignedEmployees,
                  ];
                  const newAssigned = allEmployees
                    .filter((emp) => currentIds.includes(emp.id))
                    .sort((a, b) => a.text.localeCompare(b.text));
                  setAssignedEmployees(newAssigned);
                  setAvailableEmployees(
                    allEmployees
                      .filter((emp) => !currentIds.includes(emp.id))
                      .sort((a, b) => a.text.localeCompare(b.text))
                  );
                  // Use ListBox reader instead of stale closure
                  setTimeout(() => {
                    if (isMountedRef.current) {
                      notifyParentFromListBox();
                    }
                  }, 100);
                }, 50);
              }
            }}
          />
        </Box>
      </Box>
    );
  }
);

ProjectTeamDualListBox.displayName = 'ProjectTeamDualListBox';

/**
 * ProjectTeamModal - Modal for managing project team and swim lane assignments
 */
const ProjectTeamModal = ({
  open,
  onClose,
  onBoardRefreshNeeded,
  projectId,
  statusStepSetId,
  statusStepSetName,
  projectNumber,
  projectName,
  authToken,
  userId,
}) => {
  const dualListRef = useRef();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);

  // Swim lane data
  const [swimLanes, setSwimLanes] = useState([]);
  const [swimLaneAssignments, setSwimLaneAssignments] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);

  // Duration / Type / Anchor tracking
  const [originalDurations, setOriginalDurations] = useState({});
  const [originalTypes, setOriginalTypes] = useState({});
  const [originalAnchor, setOriginalAnchor] = useState(null);
  const [isRecalculating, setIsRecalculating] = useState(false);

  // Snapshot originals once swim lanes are loaded
  useEffect(() => {
    if (open && swimLanes.length > 0) {
      const origDurs = {};
      const origTyps = {};
      let anchorId = null;

      swimLanes.forEach((lane) => {
        origDurs[lane.id] = lane.default_duration_days;
        origTyps[lane.id] = lane.swim_lane_type;
        if (lane.is_field_installation_anchor) {
          anchorId = lane.id;
        }
      });

      setOriginalDurations(origDurs);
      setOriginalTypes(origTyps);
      setOriginalAnchor(anchorId);
    }
  }, [open, swimLanes.length]); // Only re-snapshot when lanes are loaded

  // Duration / Type / Anchor handlers — update local state only
  const handleDurationChange = (laneId, newDuration) => {
    setSwimLanes((prev) =>
      prev.map((lane) =>
        lane.id === laneId
          ? { ...lane, default_duration_days: newDuration }
          : lane
      )
    );
  };

  const handleTypeChange = (laneId, newType) => {
    setSwimLanes((prev) =>
      prev.map((lane) =>
        lane.id === laneId ? { ...lane, swim_lane_type: newType } : lane
      )
    );
  };

  const handleAnchorChange = (laneId) => {
    setSwimLanes((prev) =>
      prev.map((lane) => ({
        ...lane,
        is_field_installation_anchor: lane.id === laneId,
      }))
    );
  };

  // Load swim lane data when modal opens
  useEffect(() => {
    if (open && projectId && statusStepSetId) {
      loadSwimLaneData();
    }
  }, [open, projectId, statusStepSetId]);

  const loadSwimLaneData = async () => {
    setLoading(true);
    setError(null);
    try {
      const apiClient = getApiClient(authToken);

      // Fetch tracking statuses (swim lanes) for the workflow
      const swimLanesResponse = await apiClient.get(
        `/api/projects/${projectId}/tracking-statuses`,
        { params: { statusStepSetId } }
      );

      // Fetch current swim lane assignments
      const assignmentsResponse = await apiClient.get(
        `/api/projects/${projectId}/swimlane-assignments`,
        { params: { statusStepSetId } }
      );

      // Fetch team members for the dropdown
      const teamResponse = await apiClient.get(
        `/api/projects/${projectId}/team`
      );

      setSwimLanes(swimLanesResponse.data.data?.statuses || []);
      setSwimLaneAssignments(assignmentsResponse.data.data?.assignments || []);
      setTeamMembers(teamResponse.data.data?.teamMembers || []);
    } catch (err) {
      console.error('Error loading swim lane data:', err);
      setError('Failed to load swim lane data');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      // 1. Save team member assignments (dual list)
      await dualListRef.current?.saveAssignments();
      setHasChanges(false);

      // 2. Detect duration / type / anchor changes
      const durationChanges = swimLanes
        .filter(
          (lane) => lane.default_duration_days !== originalDurations[lane.id]
        )
        .map((lane) => ({
          tracking_status_id: lane.id,
          default_duration_days: lane.default_duration_days,
        }));

      const typeChanges = swimLanes
        .filter((lane) => lane.swim_lane_type !== originalTypes[lane.id])
        .map((lane) => ({
          tracking_status_id: lane.id,
          swim_lane_type: lane.swim_lane_type,
        }));

      const currentAnchor = swimLanes.find(
        (lane) => lane.is_field_installation_anchor
      )?.id;
      const anchorChanged = currentAnchor !== originalAnchor;

      const hasDurationTypeChanges =
        durationChanges.length > 0 || typeChanges.length > 0 || anchorChanged;

      // 3. If duration/type/anchor changed, batch update + recalculate
      if (hasDurationTypeChanges) {
        setIsRecalculating(true);
        try {
          const apiClient = getApiClient(authToken);
          await apiClient.patch(
            `/api/workflow/projects/${projectId}/swim-lane-durations`,
            {
              workflow_id: statusStepSetId,
              duration_updates: durationChanges,
              type_updates: typeChanges,
              new_anchor_id: anchorChanged ? currentAnchor : null,
            }
          );
        } catch (recalcErr) {
          console.error('Failed to update swim lane durations:', recalcErr);
          setError(
            recalcErr.response?.data?.message ||
              'Failed to update swim lane durations'
          );
          setIsRecalculating(false);
          setSaving(false);
          return; // Don't close if recalculation failed
        } finally {
          setIsRecalculating(false);
        }
      }

      // 4. Refresh the board to show updated data
      if (onBoardRefreshNeeded) {
        await onBoardRefreshNeeded();
      }

      onClose();
    } catch (err) {
      console.error('Error saving project team:', err);
      setError(
        err.response?.data?.message || 'Failed to save project team settings'
      );
    } finally {
      setSaving(false);
    }
  };

  // Reload only swim lane assignments (not team members) to stay in sync with server
  // This preserves any unsaved drag-drop changes to team members
  const reloadSwimLaneAssignments = async () => {
    try {
      const apiClient = getApiClient(authToken);
      const assignmentsResponse = await apiClient.get(
        `/api/projects/${projectId}/swimlane-assignments`,
        { params: { statusStepSetId } }
      );
      setSwimLaneAssignments(assignmentsResponse.data.data?.assignments || []);
    } catch (err) {
      console.error('Error reloading swim lane assignments:', err);
    }
  };

  // Called when swim lane assignment changes (already saved via API)
  const handleSwimLaneAssignmentChange = () => {
    // Only reload swim lane assignments, NOT team members
    // Team members may have unsaved drag-drop changes
    reloadSwimLaneAssignments();
  };

  // Save pending team member changes if any
  // This is called before making swim lane assignments to ensure the resource exists
  const saveTeamMembersIfNeeded = async () => {
    if (hasChanges && dualListRef.current?.saveAssignments) {
      try {
        await dualListRef.current.saveAssignments();
        setHasChanges(false);
        return true; // Saved successfully
      } catch (err) {
        console.error('Error auto-saving team members:', err);
        return false; // Save failed
      }
    }
    return true; // No changes needed
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      {/* Recalculating overlay */}
      {isRecalculating && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            bgcolor: 'rgba(255, 255, 255, 0.9)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
          }}
        >
          <CircularProgress size={60} />
          <Typography variant="h6" sx={{ mt: 2 }}>
            Recalculating due dates...
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Please wait, updating all packages in this project
          </Typography>
        </Box>
      )}
      <DialogTitle>
        Project Team:
        {projectNumber && projectName && ` ${projectNumber} - ${projectName}`}
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box display="flex" justifyContent="center" p={4}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Box mb={3}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Drag employees between lists to add or remove them from the
                project team.
              </Typography>
              <ProjectTeamDualListBox
                ref={dualListRef}
                authToken={authToken}
                projectId={projectId}
                userId={userId}
                onAssignmentsChange={(updatedTeamMembers) => {
                  console.log('=== onAssignmentsChange received ===');
                  console.log('updatedTeamMembers:', updatedTeamMembers);
                  setHasChanges(true);
                  // If we received an updated team members list, use it
                  if (updatedTeamMembers && Array.isArray(updatedTeamMembers)) {
                    console.log('Setting teamMembers to:', updatedTeamMembers);
                    setTeamMembers(updatedTeamMembers);
                  }
                }}
              />
            </Box>

            <Divider sx={{ my: 3 }} />

            <Box>
              <Typography variant="h6" gutterBottom>
                Swim Lane Configuration
                {statusStepSetName && ` - ${statusStepSetName} Workflow`}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Assign team members to swim lanes. When a package moves to a
                swim lane, it will be auto-assigned to that swim lane's owner.
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                The anchor is the date the field installation is scheduled to
                start. All dates calculate off of this date.
              </Typography>
              <SwimLaneAssignmentTable
                swimLanes={swimLanes}
                assignments={swimLaneAssignments}
                teamMembers={teamMembers}
                onAssignmentChange={handleSwimLaneAssignmentChange}
                onBoardRefreshNeeded={onBoardRefreshNeeded}
                saveTeamMembersIfNeeded={saveTeamMembersIfNeeded}
                onDurationChange={handleDurationChange}
                onTypeChange={handleTypeChange}
                onAnchorChange={handleAnchorChange}
                isRecalculating={isRecalculating}
                projectId={projectId}
                authToken={authToken}
                userId={userId}
              />
            </Box>
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={saving || isRecalculating}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          color="primary"
          disabled={saving || loading || isRecalculating}
        >
          {saving
            ? isRecalculating
              ? 'Recalculating...'
              : 'Saving...'
            : 'Save Changes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ProjectTeamModal;
